/**
 * The catalogue of editable templates (#369).
 *
 * A merge tag that isn't real renders as an empty string — `renderTemplate` collapses
 * unknown keys silently (utils/merge-fields.ts:39). That is the right behaviour for a
 * half-filled template and the wrong behaviour for a typo, so the editor has to be able to
 * tell someone which tags actually work in the letter they are writing. This registry is
 * that list, and the API validates writes against it.
 *
 * Adding a kind is a code change on purpose: a kind only means something if something
 * sends it. Adding a per-request-type OVERRIDE of an existing kind is pure data.
 */


/** Merge tags every application-scoped template can use — the keys of ApplicationMergeVars. */
export const APPLICATION_MERGE_TAGS = [
	'associationName', 'associationAddress',
	'orgName', 'orgAddress', 'orgEmail',
	'submitterName', 'submitterEmail', 'submitterPhone',
	'propertyAddress', 'requestType', 'applicationNumber',
	'applicationDate', 'boardMeetingDate', 'decisionDate',
] as const;

/**
 * The per-user closing block (migration 266).
 *
 * Offered only to kinds a HUMAN sends. An acknowledgment or a nightly digest has no acting
 * user, so the tag would resolve to an empty string and quietly truncate the letter — worse
 * than not offering it, because the editor would list it as available.
 */
export const SIGNATURE_MERGE_TAG = 'signature';

export interface EmailTemplateKind {
	kind: string;
	label: string;
	/** When this gets sent — shown in the editor so nobody edits the wrong letter. */
	description: string;
	mergeTags: string[];
	/** False for prose that is displayed rather than emailed. */
	sendable: boolean;
}

export const EMAIL_TEMPLATE_KINDS: EmailTemplateKind[] = [
	{
		kind: 'needs_info',
		label: 'Needs Additional Info',
		description: 'Sent when a reviewer marks a request as missing requirements. '
			+ '{{missingItems}} becomes a bullet per unmet requirement.',
		// The one tag unique to this kind: without it the letter names no requirements,
		// which is the only thing it exists to do.
		mergeTags: [...APPLICATION_MERGE_TAGS, 'missingItems', SIGNATURE_MERGE_TAG],
		sendable: true,
	},
	{
		kind: 'acknowledgment',
		label: 'Acknowledging Receipt',
		description: 'Confirms to the homeowner that a submission arrived.',
		mergeTags: [...APPLICATION_MERGE_TAGS],
		sendable: true,
	},
	{
		kind: 'board_approval_pending',
		label: 'Waiting for Board Approval',
		description: 'Sent when a complete request goes to the board. '
			+ 'A PDF of the completed application is attached automatically.',
		mergeTags: [...APPLICATION_MERGE_TAGS, SIGNATURE_MERGE_TAG],
		sendable: true,
	},
	{
		kind: 'approval_letter',
		label: 'Approval Letter',
		description: 'Sent when the board approves a request.',
		mergeTags: [...APPLICATION_MERGE_TAGS, SIGNATURE_MERGE_TAG],
		sendable: true,
	},
	{
		kind: 'denial_letter',
		label: 'Denial Letter',
		description: 'Sent when the board declines a request.',
		mergeTags: [...APPLICATION_MERGE_TAGS, SIGNATURE_MERGE_TAG],
		sendable: true,
	},
	{
		kind: 'form_instructions',
		label: 'Application Form Instructions',
		description: 'Shown on the application form itself. Never emailed.',
		mergeTags: [...APPLICATION_MERGE_TAGS],
		sendable: false,
	},
];

export const isEmailTemplateKind = (kind: string): boolean =>
	EMAIL_TEMPLATE_KINDS.some((k) => k.kind === kind);

export const emailTemplateKind = (kind: string): EmailTemplateKind | null =>
	EMAIL_TEMPLATE_KINDS.find((k) => k.kind === kind) ?? null;

/** Valid tags for a kind; empty for an unknown kind so validation refuses everything. */
export const mergeTagsFor = (kind: string): string[] => emailTemplateKind(kind)?.mergeTags ?? [];

/**
 * Tags used in `text` that this kind does not define.
 *
 * Covers both failure modes: a tag the renderer WOULD substitute but this kind does not
 * define, and a tag the renderer would not recognise at all and therefore mail verbatim.
 */
export function unknownMergeTags(text: string, kind: string): string[] {
	const valid = new Set(mergeTagsFor(kind));
	const seen = new Set<string>();
	/**
	 * DELIBERATELY wider than the renderer's pattern.
	 *
	 * `renderTemplate` only substitutes `[\w.]` keys, so `{{first-name}}` or `{{Owner's
	 * Name}}` is not a tag to it — it is left alone and mailed to the homeowner verbatim.
	 * Matching only what the renderer matches meant the editor stayed silent about exactly
	 * the typos that leak raw braces into a letter.
	 */
	for (const match of String(text ?? '').matchAll(/\{\{\s*([^}]*?)\s*\}\}/g)) {
		const tag = match[1];
		if (!valid.has(tag)) seen.add(tag);
	}
	return [...seen];
}

/**
 * True when this kind CAN be signed but this body does not use `{{signature}}`.
 *
 * The mirror of `unknownMergeTags`, catching the opposite mistake. That one finds a tag that
 * renders as nothing; this one finds a MISSING tag, which is just as silent and more
 * confusing: the user sets a signature in Settings, the screen promises it appears on letters
 * they send, and this one letter keeps the old wording forever.
 *
 * It exists because migration 266 deliberately does NOT rewrite a template whose body was
 * edited after it was seeded — so "some of my letters ignore my signature" is a real,
 * expected state that someone has to be told about rather than left to discover.
 */
export function missingSignatureTag(text: string, kind: string): boolean {
	const k = emailTemplateKind(kind);
	if (!k?.sendable || !k.mergeTags.includes(SIGNATURE_MERGE_TAG)) return false;
	return !new RegExp(`\\{\\{\\s*${SIGNATURE_MERGE_TAG}\\s*\\}\\}`).test(String(text ?? ''));
}
